import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MatchingServiceListing {
  id: string;
  title: string;
  base_rate: number | null;
  service_category: string;
  city: string | null;
  country: string | null;
  company_name: string | null;
  listing_type: string;
  hero_image: string | null;
  photos: string[] | null;
}

export const useMatchingServiceListings = (
  requestCategory: string | null,
  requestListingType: string, // 'offering' or 'seeking'
  userId: string | null
) => {
  const [matchingListings, setMatchingListings] = useState<MatchingServiceListing[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (userId) {
      fetchMatchingListings();
    }
  }, [requestCategory, requestListingType, userId]);

  const fetchMatchingListings = async () => {
    if (!userId) return;

    try {
      setLoading(true);

      // For service seekers, show service offerings
      // For service providers, show service requests
      const matchingType = requestListingType === 'seeking' ? 'offering' : 'seeking';

      const { data, error } = await supabase
        .from('service_listings')
        .select('id, title, base_rate, service_category, city, country, company_name, listing_type, hero_image, photos')
        .eq('user_id', userId)
        .eq('listing_type', matchingType)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setMatchingListings(data || []);
    } catch (error) {
      console.error('Error fetching matching service listings:', error);
      toast({
        title: "Error loading listings",
        description: "Failed to load your matching service listings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return { matchingListings, loading, refetch: fetchMatchingListings };
};
